import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { type SchmancyTone, toneColors } from '@schmancy/theme/tone'

/**
 * The label mark's tone axis is the library's one tone axis — the same
 * vocabulary `<schmancy-tone-dot>`, `<schmancy-tone-spine>` and
 * `<schmancy-assist-chip>` resolve through, and `outlined` is that ladder's
 * `outline` rung under badge's own older name. Every member badge already
 * accepted still resolves to the byte-identical theme variables; the union
 * only widened, by the `info` role the theme has always carried.
 */
export type BadgeColor = SchmancyTone

export type BadgeSize = 'xs' | 'sm' | 'md'

export type BadgeShape = 'pill' | 'rounded'

// Each shape lands on a ladder rung, and the Record keeps that total: a new
// BadgeShape member fails to compile until it picks one. The literals are
// spelled out because Tailwind v4's scanner reads static strings only.
const BADGE_SHAPE_CLASS: Record<BadgeShape, string> = {
	pill: 'rounded-full',
	rounded: 'rounded-xs', // the micro rung — a badge is 7px, not a deleted 4px step
}

/**
 * @deprecated Render status, counts and annotations as ordinary text or typography.
 */
@customElement('schmancy-badge')
export class SchmancyBadgeV2 extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: inline-flex;
				flex-shrink: 0;
				width: fit-content;
			}
		`,
	]
	@property({ type: String, reflect: true })
	color: BadgeColor = 'primary'

	@property({ type: String, reflect: true })
	size: BadgeSize = 'md'

	@property({ type: String, reflect: true })
	shape: BadgeShape = 'pill'

	@property({ type: Boolean, reflect: true })
	outlined = false

	render() {
		const sizeClasses =
			this.size === 'xs'
				? 'text-xs px-1.5 py-0.5'
				: this.size === 'sm'
					? 'text-xs px-2 py-0.5'
					: 'text-sm px-2.5 py-0.5'

		return html`
			<div
				part="badge"
				class="${this.classMap({
					'inline-flex items-center justify-center font-medium leading-none': true,
					'transition-colors duration-150': true,
					'border border-solid': this.outlined,
					[sizeClasses]: true,
					[BADGE_SHAPE_CLASS[this.shape]]: true,
				})}"
				style="${this.styleMap(toneColors(this.color, this.outlined ? 'outline' : 'soft'))}"
			>
				<div part="content" class="flex items-center leading-none tracking-[0.01em]">
					<slot></slot>
				</div>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sch-badge': SchmancyBadgeV2
		'schmancy-badge': SchmancyBadgeV2
	}
}

/** @deprecated Render status, counts and annotations as ordinary text or typography. */
@customElement('sch-badge')
export class ScBadgeV2 extends SchmancyBadgeV2 {}
